import React from 'react';
import { useTranslation } from 'react-i18next';
import './NewEvent.css';
import { X, Clock, AlignLeft, Flag, MapPin, Video, Copy, Trash2 } from 'lucide-react';
import { useNewEventForm } from '../hooks/useNewEventForm';
import NeCalendarSelector from './NeCalendarSelector';
import NeGuestsField from './NeGuestsField';
import NeRecurrenceField from './NeRecurrenceField';

// frontend-restructuring Fase 3 (03.1-auth-store.md §6 grupo 1) -- presentacional, recibe
// `user` como prop de su(s) contenedor(es) (ComponentCalendar.jsx, ComponentTaskHoy.jsx,
// GlobalEventOverlay.jsx) en vez de leer `useUser()`/`context/*` directo.
// 04-enforce.md 4.1 -- todo el estado + carga inicial vive en `useNewEventForm`; el selector de
// calendario, el campo de invitados y el bloque de recurrencia son sub-componentes.
const NewEvent = ({ isOpen, onClose, initialData, onUpdate, onSave, onDelete, user }) => {
  const { t } = useTranslation('calendar');
  const f = useNewEventForm({ isOpen, initialData, onUpdate, onSave, onDelete, user });

  if (!isOpen) return null;

  const isEdit = !!(initialData && initialData.id);

  return (
    <div className="new-event-container">
      <div className="new-event-header">
        <h2>{isEdit ? t('eventModal.editTitle') : t('eventModal.createTitle')}</h2>
        <div className="ne-header-actions">
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
      </div>

      <div className="new-event-scroll-area">
        <div className="ne-section-main">
          <input
            className="ne-title-input"
            type="text"
            placeholder={t('eventModal.titlePlaceholder')}
            value={f.title}
            onChange={(e) => f.setTitle(e.target.value)}
            autoFocus
          />
          {isEdit && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
              background: '#000', color: '#888',
              fontSize: '8px', padding: '2px var(--space-2)', borderRadius: 'var(--radius-sm)',
              marginTop: 'var(--space-1)', width: 'fit-content'
            }}>
              <span>ID: {initialData.id}</span>
              <Copy
                size={8}
                style={{ cursor: 'pointer', marginLeft: '2px' }}
                onClick={() => navigator.clipboard.writeText(initialData.id)}
              />
            </div>
          )}
        </div>

        <div className="ne-section-time">
          <div className="ne-row">
            <Clock size={18} className="ne-icon" />
            <div className="ne-time-wrapper">
              <div className="ne-time-inputs">
                <div className="ne-time-field">
                  <label>{t('eventModal.start')}</label>
                  <input
                    type="datetime-local"
                    value={f.start}
                    onChange={(e) => f.setStart(e.target.value)}
                    onClick={(e) => !f.isInvited && e.target.showPicker()}
                    className="picker-input"
                    disabled={f.isInvited}
                  />
                </div>
                <span className="ne-time-arrow">→</span>
                <div className="ne-time-field">
                  <label>{t('eventModal.end')}</label>
                  <input
                    type="datetime-local"
                    value={f.end}
                    onChange={(e) => f.setEnd(e.target.value)}
                    onClick={(e) => !f.isInvited && e.target.showPicker()}
                    className="picker-input"
                    disabled={f.isInvited}
                  />
                </div>
              </div>
              {!f.isInvited && (
                <div className="ne-duration-tags">
                  {[10, 30, 60].map(mins => (
                    <span key={mins} className={`ne-tag ${f.isDuration(mins) ? 'selected' : ''}`} onClick={() => f.handleDuration(mins)}>{mins} min</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="ne-section-details">
          <NeCalendarSelector
            calendars={f.calendars}
            selectedCalendarId={f.selectedCalendarId}
            onSelect={(id) => { f.setSelectedCalendarId(id); f.setIsCalDropdownOpen(false); }}
            isOpen={f.isCalDropdownOpen}
            onToggleOpen={() => f.setIsCalDropdownOpen(!f.isCalDropdownOpen)}
            isInvited={f.isInvited}
            initialData={initialData}
            t={t}
          />

          <NeGuestsField
            guests={f.guests}
            setGuests={f.setGuests}
            guestInput={f.guestInput}
            setGuestInput={f.setGuestInput}
            suggestions={f.suggestions}
            setSuggestions={f.setSuggestions}
            allMembers={f.allMembers}
            isInvited={f.isInvited}
            t={t}
          />

          <div className="ne-row">
            <Video size={18} className="ne-icon" />
            <button className="ne-action-btn">{t('eventModal.addGoogleMeet')}</button>
          </div>

          <div className="ne-row">
            <MapPin size={18} className="ne-icon" />
            <input className="ne-ghost-input" type="text" placeholder={t('eventModal.addLocationPlaceholder')} />
          </div>

          <div className="ne-row ne-row-align-top">
            <AlignLeft size={18} className="ne-icon ne-icon-top" />
            <div
              ref={f.descriptionRef}
              className="ne-ghost-textarea"
              contentEditable
              suppressContentEditableWarning
              onInput={(e) => f.setDescription(e.currentTarget.innerHTML)}
              onClick={(e) => {
                const link = e.target.closest('a');
                if (link && link.href) {
                  if (e.ctrlKey || e.metaKey || link.target === '_blank') {
                    window.open(link.href, '_blank');
                  } else {
                    window.location.href = link.href;
                  }
                }
              }}
              style={{ overflowY: 'auto', whiteSpace: 'pre-wrap' }}
              placeholder={t('eventModal.addDescriptionPlaceholder')}
            />
          </div>
          <div className="ne-row">
            <Flag size={18} className="ne-icon" />
            <select className="ne-ghost-select">
              <option>{t('eventModal.visibility.default')}</option>
              <option>{t('eventModal.visibility.private')}</option>
              <option>{t('eventModal.visibility.public')}</option>
            </select>
          </div>

          <NeRecurrenceField
            isRecurring={f.isRecurring}
            setIsRecurring={f.setIsRecurring}
            recurrenceType={f.recurrenceType}
            setRecurrenceType={f.setRecurrenceType}
            selectedWeekDays={f.selectedWeekDays}
            setSelectedWeekDays={f.setSelectedWeekDays}
            t={t}
          />

          <div className="ne-inline-actions">
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <button className="ne-btn-text">{t('eventModal.moreOptions')}</button>
              {isEdit && (
                <>
                  {!f.isInvited ? (
                    <button
                      className="ne-icon-btn-danger"
                      onClick={f.handleDelete}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center' }}
                      title={t('eventModal.deleteEventTitle')}
                    >
                      <Trash2 size={16} />
                    </button>
                  ) : (
                    !f.showDeclineConfirm ? (
                      <button
                        className="ne-btn-danger-text"
                        onClick={f.handleDeclineClick}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '13px', fontWeight: '500' }}
                        title={t('eventModal.declineInvitationTitle')}
                      >
                        {t('eventModal.declineButton')}
                      </button>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', background: '#ffe4e6', padding: 'var(--space-1) var(--space-2)', borderRadius: 'var(--radius-sm)' }}>
                        <span style={{ color: '#991b1b', fontSize: '12px', fontWeight: '500' }}>{t('eventModal.confirmDeclineQuestion')}</span>
                        <button onClick={f.confirmDecline} style={{ background: 'transparent', border: 'none', color: '#991b1b', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}>{t('eventModal.yes')}</button>
                        <button onClick={f.cancelDecline} style={{ background: 'transparent', border: 'none', color: '#555', fontSize: '12px', cursor: 'pointer' }}>{t('eventModal.cancel')}</button>
                      </div>
                    )
                  )}
                </>
              )}
            </div>
            <div className="ne-right-btns">
              <button className="btn-cancel" onClick={onClose}>{t('eventModal.cancel')}</button>
              <button className="btn-save" onClick={f.handleInternalSave}>{t('eventModal.save')}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewEvent;
